import React from 'react';
import AdminLayout from '../../components/AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faEuroSign, faStore, faUsers } from '@fortawesome/free-solid-svg-icons';
import { AdminStatisticsDto, fetchAdminStatistics } from '@/features/adminPlatform/api';

const formatAmt = (n: number) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n);

const AdminAnalyticsPage: React.FC = () => {
  const [stats, setStats] = React.useState<AdminStatisticsDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchAdminStatistics()
      .then(setStats)
      .catch(err => setError(err?.message ?? 'Chyba načítání'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Analytika">
        <div className="bg-white p-6 rounded-lg shadow-sm text-sm text-gray-500">Načítám…</div>
      </AdminLayout>
    );
  }

  if (error || !stats) {
    return (
      <AdminLayout title="Analytika">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error ?? 'Data nejsou dostupná'}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Analytika">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card icon={faUsers} color="bg-blue-500" title="Uživatelé" value={stats.totalUsers.toLocaleString('cs-CZ')} sub={`+${stats.newUsersLast30Days} za 30 dní`} />
          <Card icon={faStore} color="bg-purple-500" title="Salony" value={stats.totalSalons.toLocaleString('cs-CZ')} />
          <Card
            icon={faCalendarCheck}
            color="bg-green-500"
            title="Rezervace celkem"
            value={stats.totalBookings.toLocaleString('cs-CZ')}
            sub={`+${stats.bookingsLast30Days} za 30 dní`}
          />
          <Card icon={faEuroSign} color="bg-yellow-500" title="Tržby celkem" value={formatAmt(stats.totalRevenue)} />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Top salony podle rezervací</h2>
          </div>
          {stats.topSalons.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">Žádná data.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rezervací</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tržby</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.topSalons.map(s => (
                  <tr key={s.salonId}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.salonName}</td>
                    <td className="px-4 py-3 text-sm">{s.bookingCount.toLocaleString('cs-CZ')}</td>
                    <td className="px-4 py-3 text-sm">{formatAmt(s.totalRevenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const Card: React.FC<{ icon: any; color: string; title: string; value: string; sub?: string }> = ({ icon, color, title, value, sub }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-xs text-green-600 mt-1">{sub}</p>}
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        <FontAwesomeIcon icon={icon} className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

export default AdminAnalyticsPage;
